import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Headset } from "lucide-react";
import { AccountMenu } from "./AccountMenu";
import { SyncIndicator } from "./SyncIndicator";
import { ghostButtonClass, LanguageSwitcher } from "./ui";
import { landingPathFor } from "../lib/landing";
import type { Role } from "../types";

// Teachers see the brand on their landing page and a back button on subsequent screens.
export function AppHeader({
  role,
  onLogout,
  currentTitle,
}: {
  role: Role;
  onLogout: () => void;
  currentTitle?: string | null;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;
  const helpReturn = location.state?.helpReturn as
    { to: string; label: string; state?: unknown } | undefined;
  // The completion screen has its own "Back to home" button, so no back link here.
  const onCompletionScreen = /^\/modules\/[^/]+\/complete$/.test(pathname);
  const showBack =
    role === "teacher" && pathname !== "/pd-days" && pathname !== "/" && !onCompletionScreen;
  // Every screen of a PD Day (its content page, modules, completion) has
  // one way back: "← PD Day 1", to the home screen.
  const inPDDay = pathname.startsWith("/modules/") || pathname.startsWith("/pd-day-modules/");
  const backLink =
    pathname === "/helpline" && helpReturn?.to
      ? { to: helpReturn.to, labelKey: "", label: helpReturn.label }
      : pathname.startsWith("/helpline/")
        ? { to: "/helpline", labelKey: "navHelpline" }
        : { to: "/pd-days", labelKey: "backToPDDays" };
  const screenLabelKeys: Record<string, string> = {
    "/history": "progress",
    "/profile": "navProfile",
    "/schedule": "navSchedule",
    "/helpline": "navHelpline",
  };
  const helpState = pathname.startsWith("/helpline")
    ? location.state
    : {
        helpReturn: {
          to: `${pathname}${location.search}${location.hash}`,
          label: currentTitle || t(screenLabelKeys[pathname] || "backToPDDays"),
          state: location.state,
        },
      };

  return (
    <header
      dir="ltr"
      // The brand/menu chrome must look identical in both languages — pin it
      // to the sans font so it doesn't pick up the Urdu body's Nastaliq
      // font-family (which also carries Latin glyphs and would re-shape
      // "CPD Training" here).
      style={{ fontFamily: "var(--font-sans)" }}
      className="fixed top-0 left-1/2 z-50 w-full max-w-md -translate-x-1/2 bg-surface/85 shadow-raised backdrop-blur-xl"
    >
      <div className="flex h-16 items-center justify-between px-4 pt-[env(safe-area-inset-top,0px)]">
        {showBack ? (
          <NavLink
            to={backLink.to}
            state={pathname === "/helpline" ? helpReturn?.state : location.state}
            className={`${ghostButtonClass} min-w-0 pe-2`}
          >
            <ChevronLeft size={16} aria-hidden="true" className="shrink-0" />
            <span className="truncate">
              {inPDDay && currentTitle
                ? currentTitle
                : "label" in backLink
                  ? backLink.label
                  : t(backLink.labelKey)}
            </span>
          </NavLink>
        ) : (
          <NavLink
            to={landingPathFor(role)}
            className="flex items-center gap-2"
            aria-label={t("goHome")}
          >
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent-brand text-base font-bold text-content-on-accent shadow-sm">
              C
            </span>
            <span className="flex flex-col leading-none">
              <strong className="text-sm font-bold tracking-tight text-app-ink">
                CPD <span className="text-accent">Training</span>
              </strong>
              <small className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-app-muted">
                {role === "facilitator" ? "Facilitator" : "Teacher"}
              </small>
            </span>
          </NavLink>
        )}
        <div className="flex shrink-0 items-center gap-2">
          {role === "teacher" && <SyncIndicator />}
          <LanguageSwitcher />
          {(showBack || role === "teacher") && (
            <NavLink
              to="/helpline"
              state={helpState}
              replace={pathname.startsWith("/helpline")}
              aria-label={t("navHelpline")}
              title={t("navHelpline")}
              className="grid h-8 w-8 place-items-center rounded-full bg-accent-soft text-accent transition-colors hover:bg-app-field"
            >
              <Headset size={18} aria-hidden="true" />
            </NavLink>
          )}
          <AccountMenu
            role={role}
            onLogout={onLogout}
            showHelpline={!showBack && role !== "teacher"}
          />
        </div>
      </div>
    </header>
  );
}
